import React, { useState } from 'react';
import pb from '../api/pocketbase';
import { exportToExcelCharges, exportToPDF, exportToSVG } from './exportation/exportUtils';
import ExportModal from './exportation/exportModal';

export default function FiltreDepensesChef({ projectId, chefChantierNom, nomProjet }) {
  const [filtreDateDebut, setFiltreDateDebut] = useState('');
  const [filtreDateFin, setFiltreDateFin] = useState('');
  const [depensesFiltrees, setDepensesFiltrees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedProjectData, setSelectedProjectData] = useState(null);
  const [hasFiltered, setHasFiltered] = useState(false); // Ajout
  const [error, setError] = useState(""); // Ajout pour le message d'erreur

  // Pour la modale de description
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");

  // Récupérer les dépenses validées DG pour ce projet et ce chef (local)
  const fetchDepenses = async () => {
    setLoading(true);
    try {
      let filter = [];

      // Utilise nomProjet pour filtrer
      if (nomProjet) filter.push(`nomProjet="${nomProjet}"`);
      if (chefChantierNom) filter.push(`chefChantier="${chefChantierNom}"`);
      if (filtreDateDebut) filter.push(`dateValidation >= "${filtreDateDebut}"`);
      if (filtreDateFin) filter.push(`dateValidation <= "${filtreDateFin}T23:59:59"`);

      const filterString = filter.join(' && ');

      const result = await pb.collection('depensesValideesDG').getFullList({
        filter: filterString,
        sort: '-dateValidation'
      });

      setDepensesFiltrees(result);
    } catch (error) {
      console.error("Erreur PocketBase:", error);
      setDepensesFiltrees([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltrer = (e) => {
    e.preventDefault();
    if (!filtreDateDebut || !filtreDateFin) {
      setError("Veuillez renseigner la date de début et la date de fin.");
      return;
    }
    setError("");
    setHasFiltered(true);
    fetchDepenses();
  };

  const handleReset = () => {
    setFiltreDateDebut('');
    setFiltreDateFin('');
    setDepensesFiltrees([]);
    setHasFiltered(false);
    setError("");
  };

  // Regroupement par projet et rubrique
  const groupedByProjet = depensesFiltrees.reduce((acc, dep) => {
    const projet = dep.nomProjet || nomProjet || "-";
    const rubrique = Array.isArray(dep.rubriques) && dep.rubriques[0]?.rubrique
      ? dep.rubriques[0].rubrique
      : "Autre";
    if (!acc[projet]) acc[projet] = {};
    if (!acc[projet][rubrique]) acc[projet][rubrique] = [];
    acc[projet][rubrique].push(dep);
    return acc;
  }, {});

  const total = depensesFiltrees.reduce((sum, d) => sum + Number(d.montant || 0), 0);

  // Ajout fonction d'export
  const handleExportProject = () => {
    setSelectedProjectData({
      projet: nomProjet,
      chefChantier: chefChantierNom,
      depenses: depensesFiltrees,
      total,
      filtreDateDebut,
      filtreDateFin
    });
    setShowExportModal(true);
  };

  const handleExport = (format) => {
    if (!selectedProjectData) return;
    switch (format) {
      case 'excel':
        exportToExcelCharges(selectedProjectData);
        break;
      case 'pdf':
        exportToPDF(selectedProjectData);
        break;
      case 'svg':
        exportToSVG(selectedProjectData);
        break;
      default:
        exportToExcelCharges(selectedProjectData);
    }
    setShowExportModal(false);
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-xl shadow-lg border-0 shadow-gray-200">
        {/* Formulaire de filtre */}
        <form className="flex flex-wrap gap-4 items-end p-6" onSubmit={handleFiltrer}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
            <input
              type="date"
              value={filtreDateDebut}
              onChange={e => setFiltreDateDebut(e.target.value)}
              className="border rounded px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
            <input
              type="date"
              value={filtreDateFin}
              onChange={e => setFiltreDateFin(e.target.value)}
              className="border rounded px-3 py-2"
              required
            />
          </div>
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold"
            disabled={!filtreDateDebut || !filtreDateFin}
          >
            Filtrer
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded font-semibold"
          >
            Réinitialiser
          </button>
          {error && (
            <div className="w-full text-red-600 font-medium mt-2">{error}</div>
          )}
        </form>

        {/* Affichage conditionnel */}
        {hasFiltered ? (
          loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            Object.keys(groupedByProjet).length === 0 ? (
              <div className="text-center py-12 text-gray-500">Aucun résultat pour ce filtre.</div>
            ) : (
              Object.entries(groupedByProjet).map(([projet, rubriques]) => {
                const chefChantier = chefChantierNom;
                const depensesProjet = Object.values(rubriques).flat();
                const totalProjet = depensesProjet.reduce((sum, dep) => sum + Number(dep.montant || 0), 0);
                return (
                  <div key={projet} className="rounded-xl shadow-lg bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 mb-2">
                    <div className="flex items-center px-6 py-4">
                      <span className="inline-block bg-blue-600 text-white rounded-full px-4 py-2 font-bold text-lg shadow">
                        {projet}
                      </span>
                      <span className="ml-4 text-blue-900 font-semibold text-base">
                        Chef de chantier : {chefChantier}
                      </span>
                      <span className="ml-auto text-blue-900 font-bold text-base mr-4">
                        Total projet : {totalProjet.toLocaleString()} FCFA
                      </span>
                      {/* Bouton Extraire */}
                      <button
                        onClick={() => {
                          setSelectedProjectData({
                            projet,
                            chefChantier,
                            depenses: depensesProjet,
                            total: totalProjet,
                            filtreDateDebut,
                            filtreDateFin
                          });
                          setShowExportModal(true);
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2 shadow"
                        disabled={depensesProjet.length === 0}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Extraire</span>
                      </button>
                    </div>
                    {/* Tableau des rubriques pour ce projet */}
                    <table className="min-w-full bg-transparent">
                      <tbody>
                        {Object.entries(rubriques).map(([rubrique, deps]) => (
                          <React.Fragment key={rubrique}>
                            <tr>
                              <td colSpan={8} className="py-0">
                                <div
                                  className="flex items-center gap-3 px-6 py-3 my-2 rounded-lg shadow-md"
                                  style={{
                                    background: "linear-gradient(90deg, #2563eb 0%, #60a5fa 100%)",
                                    boxShadow: "0 4px 16px 0 #2563eb22"
                                  }}
                                >
                                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/30 mr-2">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2a4 4 0 014-4h4M7 7h.01M7 11h.01M7 15h.01" />
                                    </svg>
                                  </span>
                                  <span className="font-bold text-white text-lg tracking-wide drop-shadow">{rubrique}</span>
                                  <span className="ml-auto font-bold text-white text-base drop-shadow">
                                    {deps.reduce((sum, dep) => sum + Number(dep.montant || 0), 0).toLocaleString()} FCFA
                                  </span>
                                </div>
                              </td>
                            </tr>
                            {deps.map((dep, idx) => (
                              <tr key={dep.id} className={`transition ${idx % 2 === 0 ? "bg-blue-50/60" : "bg-white"}`}>
                                <td className="px-6 py-3"></td>
                                <td className="px-6 py-3 text-blue-900 font-semibold">
                                  {Array.isArray(dep.rubriques) && dep.rubriques[0]?.sousRubrique
                                    ? dep.rubriques[0].sousRubrique
                                    : "-"}
                                </td>
                                <td className="px-6 py-3 text-gray-700">
                                  {dep.fournisseur || "-"}
                                </td>
                                <td className="px-6 py-3 text-gray-700">
                                  {dep.description ? (
                                    <button
                                      className="text-blue-600 underline font-medium"
                                      onClick={() => {
                                        setSelectedDescription(dep.description);
                                        setShowDescriptionModal(true);
                                      }}
                                    >
                                      Voir
                                    </button>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-6 py-3 text-right font-bold text-blue-900">
                                  {Number(dep.montant).toLocaleString()} <span className="text-xs font-normal text-gray-500">FCFA</span>
                                </td>
                                <td className="px-6 py-3 text-center text-gray-600">
                                  {dep.dateValidation
                                    ? new Date(dep.dateValidation).toLocaleDateString('fr-FR', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                      })
                                    : "-"}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })
            )
          )
        ) : (
          <div className="text-center py-12 text-gray-400">
            Veuillez choisir vos filtres puis cliquer sur <b>Filtrer</b> pour voir les résultats.
          </div>
        )}

        {/* Modale description et export */}
        {showDescriptionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Description détaillée</h3>
                <button
                  onClick={() => setShowDescriptionModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <span className="text-xl">✗</span>
                </button>
              </div>
              <div className="px-6 py-4">
                <p className="text-gray-700 whitespace-pre-line">{selectedDescription}</p>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setShowDescriptionModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          projectName={selectedProjectData?.projet}
        />
      </div>
    </div>
  );
}
